import { MONEY_SCALE, QUANTITY_SCALE, UNIT_PRICE_SCALE, fromString, round, toString, zero, } from '@aeris/money';
/**
 * Sales documents.
 *
 * Master Specification section 17 opens with a prohibition rather than a
 * feature list:
 *
 *   > O fluxo de vendas NÃO deve ser hardcoded para um único segmento.
 *
 * and gives two workflows that must both work — the wholesale chain
 * (`Quote → SalesOrder → … → Shipment`) and the shop (`Sale → Payment →
 * Delivery`). ADR-0017 settles the shape: ONE document carrying a declared
 * `kind`, with the legal transitions in a single table in code (see
 * `transitions.ts`).
 *
 * This package is pure. It depends only on `@aeris/money`, so a browser
 * building a cart and the server storing it compute the same total from the
 * same code, rather than from two implementations that agree until they do not.
 */
// ---------------------------------------------------------------------------
// Kinds and statuses
// ---------------------------------------------------------------------------
/**
 * Which document this is.
 *
 * A quote is a proposal. An order is a commitment to deliver. A sale is a
 * transaction that is already over by the time it is recorded — which is what a
 * till produces, and why the shop workflow is one transition rather than six.
 *
 * Adding a fourth is one entry here and one row in `TRANSITIONS`. That is
 * deliberately cheap; ADR-0017 rejected both a table per document type and a
 * configurable workflow engine for the same reason.
 */
export const SALES_DOCUMENT_KINDS = ['quote', 'order', 'sale'];
export const isSalesDocumentKind = (value) => typeof value === 'string' &&
    SALES_DOCUMENT_KINDS.includes(value);
/**
 * Every status any kind can hold.
 *
 * One list rather than one per kind, because the column is one column. Which of
 * these a given kind may actually reach is decided by `TRANSITIONS`, and a
 * status that is legal for a quote is not thereby legal for a sale.
 */
export const SALES_DOCUMENT_STATUSES = [
    /** Being written. The only status in which lines may still change. */
    'draft',
    /** A quote that has gone to the customer. */
    'sent',
    /** The customer said yes. It has not become an order yet. */
    'accepted',
    /** The customer said no. */
    'rejected',
    /** The prazo passed. The captured price no longer holds — see `isExpired`. */
    'expired',
    /** An order that is committed: numbered, and reserving stock if it was marked to. */
    'confirmed',
    /** An order whose goods have left. */
    'fulfilled',
    /** A sale that is done. The shop's terminal state. */
    'completed',
    /** A quote that became an order. Terminal, and points at what it became. */
    'converted',
    'cancelled',
];
export const isSalesDocumentStatus = (value) => typeof value === 'string' &&
    SALES_DOCUMENT_STATUSES.includes(value);
/**
 * What the document currently holds against the stock ledger.
 *
 * Stored rather than derived from the movements, because "should cancelling
 * this give stock back, and how?" must be answerable without replaying a
 * ledger — and because the two possible answers (release a reservation, or
 * write reversing movements) are different operations with different effects.
 */
export const STOCK_STATES = [
    'none',
    'reserved',
    'consumed',
    'released',
    'reversed',
];
/**
 * Where a line's price came from.
 *
 * `manual` is somebody typing a number, which is legitimate and must be
 * distinguishable from a resolved price — "why did this cost that?" has a
 * different answer in each case, and a year later nobody remembers.
 */
export const PRICE_BASES = ['base-price', 'quantity-tier', 'manual'];
// ---------------------------------------------------------------------------
// Amounts
// ---------------------------------------------------------------------------
export const ZERO_AMOUNT = zero(MONEY_SCALE);
export const ZERO_LINE_QUANTITY = zero(QUANTITY_SCALE);
/**
 * Reads an amount from a string, at the given scale.
 *
 * A string, always: NUMERIC arrives from the driver as text and travels the
 * wire as text, precisely so no boundary can round it (ADR-0016). Returns null
 * rather than throwing, because an unparseable amount is ordinary user input
 * and not a programming error.
 */
const parseAt = (value, scale) => {
    try {
        return round(fromString(value.trim()), scale, 'half-up');
    }
    catch {
        return null;
    }
};
export const parseAmount = (value) => parseAt(value, MONEY_SCALE);
export const parseUnitPrice = (value) => parseAt(value, UNIT_PRICE_SCALE);
export const parseQuantity = (value) => parseAt(value, QUANTITY_SCALE);
/** The wire and database format. Never a JSON number. */
export const amountToString = (value) => toString(value);
// ---------------------------------------------------------------------------
// The prazo da proposta
// ---------------------------------------------------------------------------
/**
 * Whether a document's deadline has passed.
 *
 * Half-open, `[issued, validUntil)`, for the same reason a price window is:
 * at the instant a proposal expires it is expired, and a rule that made the
 * boundary belong to both sides would make a single microsecond of the year
 * ambiguous.
 *
 * A document with no `validUntil` never expires. Null means "no deadline", not
 * "expires now" — a quote with no stated prazo is one somebody chose not to put
 * a date on.
 */
export const isExpired = (validUntil, at = new Date()) => validUntil !== null && at.getTime() >= validUntil.getTime();
/**
 * Whether the captured price on this document still holds.
 *
 * This is decision 3 in F013, and it is a property of the DOCUMENT rather than
 * a policy buried in a converter. Within the prazo the captured price is
 * honoured without question — that is what a proposal is. After it, converting
 * requires re-pricing rather than silently honouring a price the business no
 * longer offers, or silently updating one the customer was quoted.
 *
 * A document already marked `expired` does not become honourable again by
 * having its date extended; the status is checked first for that reason.
 */
export const honoursCapturedPrice = (document, at = new Date()) => document.status !== 'expired' && !isExpired(document.validUntil, at);
//# sourceMappingURL=entities.js.map